import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "../data/content";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const logoY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-samatva-peach via-samatva-cream to-samatva-lavender" />
      <div className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-samatva-lilac/25 blur-3xl" />
      <div className="absolute -bottom-40 -left-24 w-[30rem] h-[30rem] rounded-full bg-samatva-blush/40 blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto w-full px-6 md:px-12 pt-32 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <h1 className="font-serif text-samatva-ink text-5xl sm:text-7xl md:text-[5.5rem] font-light tracking-tight leading-[0.95]">
            <MaskedLine delay={0.15}>Well-being is an</MaskedLine>
            <MaskedLine delay={0.3}>
              umbrella term. <em className="text-samatva-plum">So is</em>
            </MaskedLine>
            <MaskedLine delay={0.45}>
              <em className="text-samatva-plum">Samatva!</em>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-subtitle"
            className="mt-8 max-w-md text-base md:text-lg font-light leading-relaxed text-samatva-muted"
          >
            An ecosystem for holistic well-being — drawing from Indian
            wisdom, contemporary knowledge, lived experiences, and meaningful
            conversations.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-join-button"
              onClick={() =>
                window.__lenis?.scrollTo("#join", { duration: 1.6 })
              }
              className="group px-8 py-4 rounded-full bg-samatva-ink text-samatva-cream text-sm tracking-wide hover:bg-samatva-plum transition-colors duration-300 flex items-center gap-2"
            >
              Join the community
              <ArrowDown
                size={16}
                className="transition-transform duration-300 group-hover:translate-y-1"
              />
            </button>
            <button
              data-testid="hero-explore-button"
              onClick={() =>
                window.__lenis?.scrollTo("#pillars", { duration: 1.6 })
              }
              className="px-8 py-4 rounded-full border border-samatva-ink/30 text-samatva-ink text-sm tracking-wide hover:border-samatva-plum hover:text-samatva-plum transition-colors duration-300"
            >
              Explore the pillars
            </button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="overflow-hidden rounded-t-full rounded-b-[3rem] aspect-[3/4] shadow-[0_40px_80px_-30px_rgba(46,42,92,0.35)]">
              <motion.img
                style={{ y: imgY }}
                src={IMAGES.hero}
                alt="Woman meditating beside a peaceful river"
                className="w-full h-[120%] object-cover scale-110"
              />
            </div>
            <motion.div
              style={{ y: logoY }}
              className="absolute -left-16 bottom-10 w-36 h-36 rounded-full bg-samatva-cream p-3 shadow-[0_20px_50px_-20px_rgba(46,42,92,0.4)]"
            >
              <motion.img
                src="/samatva-logo.jpeg"
                alt="Samatva lotus logo"
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                className="w-full h-full rounded-full object-cover mix-blend-multiply"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-samatva-plum"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
};
